// Parent product component
if (!customElements.get('component-pdp-simple')) {
  customElements.define('component-pdp-simple', class PDPSimple extends HTMLElement {
    constructor() {
      super();
      // State
      this.state = {
        product: {}
      }
      // Props
      this.props = {
        productHandle: this.dataset.productHandle,
        emailKey: ''
      }
      // DOM
      this.dom = {
        template: this.querySelector('template')
      }
    }
    async connectedCallback() {
      this.state.product = await this.getProduct();
      this.dispatchEvent(new CustomEvent('render-product'));
    }
    async getProduct() {
      this.dispatchEvent(new CustomEvent('product-loading'));
      // Get product data
      let response = await fetch(`${window.Shopify.routes.root}products/${this.props.productHandle}?view=pdp-simple`, {
        method: "get"
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      response = await response.json();
      return response.product;
    }
  });
}

// Boilerplate class used for all product subcomponents
class PDPProductSubComponent extends HTMLElement {
  constructor() {
    super();

    this.props = {
      hydration: this.dataset.hydration && this.dataset.hydration === 'true' ? true : false
    }

    this.dom = {
      parentComponent: this.closest('component-pdp-simple'),
      template: this.querySelector('.js-template'),
      skeletonTemplate: this.querySelector('.js-template-skeleton')
    }

    if (this.props.hydration) {
      const template = document.createElement('template');
      template.content.appendChild(this.dom.template.cloneNode(true).querySelector('.js-container'));
      this.template = template;
    } else {
      this.template = this.dom.template;
    }

    this.dom.parentComponent.addEventListener('render', (event) => {
      // Render all components by default
      let componentsToRender = 'all';
      if (event.detail && event.detail.componentsToRender) {
        componentsToRender = event.detail.componentsToRender;
      }
      // Support ability to re-render only certain components using the component name
      const name = this.tagName.toLowerCase().replace(/-/g, '').replace(/component/g, '');
      const components = componentsToRender.toLowerCase();
      if (components === 'all' || components.includes(name)) {
        if (this.querySelector('.js-container')) {
          this.querySelector('.js-container').remove();
        }
        this.render();
      }
    });
    // Event to render all components after product is fetched
    this.dom.parentComponent.addEventListener('render-product', () => {
      this.render();
    });
  }
  append(template) {
    if (this.querySelector('.js-template') && this.querySelector('.js-template').nodeName === 'DIV') {
      this.querySelector('.js-template').remove();
    }
    this.appendChild(template);
  }
  getProduct() {
    return this.dom.parentComponent.state.product;
  }
  getVariant() {
    return this.dom.parentComponent.state.product.variant;
  }
}

if (!customElements.get('component-pdp-product-gallery')) {
  customElements.define('component-pdp-product-gallery', class PDPProductGallery extends PDPProductSubComponent {
    constructor() {
      super();
      this.state = {
        firstRender: true,
        mediaIndex: 0,
        models: {},
        activeModelId: ''
      }
      this.props.badgePrefix = this.dataset.badgePrefix;
    }
    async render(modelMedia) {
      const template = this.template.content.cloneNode(true);
      // Reset media index to show first image on color change
      this.state.mediaIndex = 0;
      if (!modelMedia) {
        this.state.activeModelId = '';
      }
      // Default image size matches size in 'json-color' snippet
      this.defaultImgSize = '300';
      if (this.getProduct()) {
        let media = modelMedia ? modelMedia.slice() : this.getProduct().media.slice();
        // Move main media to end of grid
        if (media.length > 2) {
          if (!media[0].alt.includes('model') || media[0].src.includes('PDP_FLAT')) {
            media.push(media.shift());
          }
        }
        this.state.media = media;
        if (this.props.hydration && !this.state.firstRender) {
          if (window.innerWidth < 768) {
            this.renderMobileGallery(template);
          } else {
            this.renderDesktopGallery(template);
          }
          this.append(template);
        }
        this.renderControls(this);
        const badge = this.getBadge();
        if (badge) {
          this.renderBadge(this, badge);
        }
      }
      this.afterRender();
    }
    renderDesktopGallery(template) {
      this.renderThumbnails(template);
      this.renderMedia(template);
    }
    renderThumbnails(template) {
      template.querySelectorAll('.js-thumbnail').forEach(item => item.remove());
      this.state.media.forEach((media, index) => {
        const thumbnail = document.createElement('img');
        thumbnail.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
        thumbnail.dataset.sizes = 'auto';
        thumbnail.dataset.src = media.src;
        thumbnail.alt = media.alt;
        thumbnail.width = media.width;
        thumbnail.height = media.height;
        thumbnail.dataset.index = index;
        thumbnail.classList.add('lazyload', 'thumbnail', 'js-thumbnail');
        template.querySelector('.js-thumbnails').appendChild(thumbnail);
      });
    }
    renderMedia(template) {
      if (template.querySelector('.js-media')) template.querySelector('.js-media').remove();
      let mediaEl;
      const media = this.state.media[this.state.mediaIndex];
      if (!media) return;
      if (media.type === 'video' && media.videoSrc) {
        mediaEl = document.createElement('video');
        const sourceEl = document.createElement('source');
        mediaEl.controls = true;
        mediaEl.playsinline = true;
        sourceEl.src = media.videoSrc;
        sourceEl.type = `video/${media.videoFormat}`;
        mediaEl.appendChild(sourceEl);
      } else {
        mediaEl = document.createElement('img');
        mediaEl.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
        mediaEl.dataset.sizes = 'auto';
        mediaEl.classList.add('lazyload');
        mediaEl.width = media.width;
        mediaEl.height = media.height;
        mediaEl.alt = media.alt;
      }
      mediaEl.classList.add('js-media', 'media');
      template.querySelector('.js-media-container').appendChild(mediaEl);
    }
    renderControls(template) {
      if (this.state.media.length > 1) {
        template.querySelector('.js-gallery-prev').classList.remove('hidden');
        template.querySelector('.js-gallery-next').classList.remove('hidden');
      } else {
        template.querySelector('.js-gallery-prev').classList.add('hidden');
        template.querySelector('.js-gallery-next').classList.add('hidden');
      }
    }
    renderMobileGallery(template) {
      // Remove liquid rendered elements
      template.querySelectorAll('.js-gallery-mobile > *:not(template)').forEach(item => item.remove());
      template.querySelectorAll('.js-gallery-mobile-dots > *:not(template)').forEach(item => item.remove());
      // Ignore first image if first render (first image is rendered via liquid)
      if (this.state.firstRender) {
        this.state.media.shift();
      }
      this.state.media.forEach((media, index) => {
        let mobileMedia;
        if (media.type == 'video') {
          mobileMedia = document.createElement('video');
          const sourceEl = document.createElement('source');
          mobileMedia.controls = true;
          mobileMedia.playsinline = true;
          mobileMedia.classList.add('js-mobile-media', 'mobile-media');
          sourceEl.src = media.videoSrc;
          sourceEl.type = `video/${media.videoFormat}`;
          mobileMedia.appendChild(sourceEl);
        } else {
          mobileMedia = document.createElement('img');
          mobileMedia.sizes = '100vw';
          mobileMedia.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
          mobileMedia.alt = media.alt;
          mobileMedia.width = media.width;
          mobileMedia.height = media.height;
          mobileMedia.classList.add('mobile-media', 'js-mobile-media');
          if (index > 1) {
            mobileMedia.loading = 'lazy';
          }
        }
        mobileMedia.dataset.index = index;
        mobileMedia.classList.add(`js-mobile-media-${index}`);
        template.querySelector('.js-gallery-mobile').appendChild(mobileMedia);
        // Mobile dots
        const mobileDotTemplate = template.querySelector('.js-template-gallery-mobile-dot').content.cloneNode(true);
        mobileDotTemplate.querySelector('.js-dot').classList.add(`js-dot-${index}`);
        mobileDotTemplate.querySelector('.js-dot').dataset.index = index;
        template.querySelector('.js-gallery-mobile-dots').appendChild(mobileDotTemplate);
      });
    }
    getBadge() {
      const tags = this.getProduct().tags;
      let badge;
      for (let tag in tags) {
        if (typeof tags[tag] === 'string' && tags[tag].includes(this.props.badgePrefix)) {
          badge = tags[tag].replace(this.props.badgePrefix, '');
          break;
        }
      }
      return badge;
    }
    renderBadge(template, badge) {
      function updateBadge() {
        const badgeTemplate = template.querySelector('.js-template-gallery-badge').content.cloneNode(true);
        badgeTemplate.querySelector('.js-gallery-badge').innerHTML = badge;
        return badgeTemplate;
      }
      template.querySelector('.js-media-container-footer').append(updateBadge());
      template.querySelector('.js-gallery-mobile .js-media-container-footer').append(updateBadge());
    }
    handleMobileGallery() {
      this.querySelectorAll('.js-mobile-media').forEach(img => {
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });
      // Use intersection observer to set up navigation dots on mobile gallery
      let options = {
        root: this.querySelector('.js-gallery-mobile'),
        rootMargin: "0px",
        threshold: 0.6
      };
      let callback = entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.querySelectorAll('.js-dot').forEach(dot => {
              dot.classList.remove('active');
            });
            this.querySelector(`.js-dot-${entry.target.dataset.index}`).classList.add('active');
            if (entry.target.alt) {
              const alt = entry.target.alt?.trim().split('model')[1];
              if (alt) {
                this.state.mediaIndex = entry.target.dataset.index;
              }
            }
          }
        });
      };
      let observer = new IntersectionObserver(callback, options);
      this.querySelectorAll('.js-mobile-media').forEach(img => {
        observer.observe(img);
      });
      this.querySelectorAll('.js-dot').forEach(dot => {
        dot.addEventListener('click', () => {
          const index = dot.dataset.index;
          this.querySelector(`.js-mobile-media-${index}`).scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
        })
      });
    }
    renderZoomGallery(img) {
      const template = this.querySelector('.js-template-gallery-zoom').content.cloneNode(true);
      for (let imgData of this.state.media) {
        if (imgData.type != 'video') {
          // Add thumbnails
          const thumbTemplate = template.querySelector('.js-template-zoom-thumb').content.cloneNode(true);
          const thumbImg = thumbTemplate.querySelector('.js-zoom-thumb');
          thumbImg.src = imgData.src;
          template.querySelector('.js-zoom-thumbs-container').appendChild(thumbTemplate);
        }
      }
      // Add main image
      const imgTemplate = template.querySelector('.js-template-zoom-image').content.cloneNode(true);
      const zoomImg = imgTemplate.querySelector('.js-zoom-img');
      // Set main image to clicked image
      zoomImg.dataset.srcset = img.dataset.srcset;
      zoomImg.dataset.sizes = 'auto';
      template.querySelector('.js-zoom-img-container').appendChild(imgTemplate);
      this.querySelector('.js-gallery-desktop-container').appendChild(template);
      this.querySelector('.js-gallery-zoom-container').classList.remove('hidden');
      document.querySelector('body').classList.add('overflow-hidden', 'zoom-enabled');
      this.querySelector('.js-zoom-img').onload = () => {
        this.querySelector('.js-zoom-img').classList.add('loaded');
      }
    }
    handleZoomGallery() {
      this.querySelectorAll('img.js-media').forEach(el => {
        el.addEventListener('click', () => {
          let img = el.parentNode.querySelector('.js-media');
          this.renderZoomGallery(img);
          this.querySelectorAll('.js-zoom-thumb').forEach(thumb => {
            thumb.addEventListener('click', () => {
              this.querySelector('.js-zoom-img').srcset = getSrcset(thumb.src, this.defaultImgSize);
              this.querySelector('.js-zoom-img').src = img.src;
            });
            thumb.addEventListener('mouseover', () => {
              this.querySelector('.js-zoom-img').srcset = getSrcset(thumb.src, this.defaultImgSize);
              this.querySelector('.js-zoom-img').src = thumb.src;
            });
          });
          this.querySelectorAll('.js-zoom-close').forEach(el => {
            el.addEventListener('click', () => {
              this.closeZoom();
            });
          });
          this.querySelector('.js-zoom-img-container').addEventListener('click', (event) => {
            if (!event.target.classList.contains('js-zoom-img')) {
              this.closeZoom();
            }
          })
        });
      });
    }
    handleStylistChat() {
      if (this.querySelector('.js-chat-stylist')) {
        this.querySelector('.js-chat-stylist').addEventListener('click', () => {
          document.querySelector('[data-chat-action="openChat"]').click();
        });
      }
      if (window.theme?.chat?.isOnline) {
        if (this.querySelector('.js-chat-stylist-container')) {
          this.querySelector('.js-chat-stylist-container').classList.remove('hidden');
        }
      }
    }
    closeZoom() {
      this.querySelector('.js-gallery-zoom-container').classList.add('hidden');
      document.querySelector('body').classList.remove('overflow-hidden', 'zoom-enabled');
      this.querySelector('.js-gallery-zoom-container').remove();
    }
    afterRender() {
      this.state.firstRender = false;
      this.handleMobileGallery();
      this.handleThumbnails();
      this.handleControls();
      this.handleZoomGallery();
      this.handleStylistChat();
      if (window.theme?.loyalty?.is_rewards_banner_displayed) {
        const parentNode = this.parentNode;
        if (parentNode) parentNode.style.top = '213px';
      }
    }
    handleThumbnails() {
      this.querySelectorAll('.js-thumbnail').forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => {
          this.state.mediaIndex = index;
          this.renderMedia(this);
          this.handleZoomGallery();
        })
      });
    }
    handleControls() {
      const prevButton = this.querySelector('.js-gallery-prev');
      const nextButton = this.querySelector('.js-gallery-next');
      const updateMediaIndex = (increment) => {
          this.state.mediaIndex += increment;
          if (this.state.mediaIndex >= this.state.media.length) {
            this.state.mediaIndex = 0;
          } else if (this.state.mediaIndex < 0) {
            this.state.mediaIndex = this.state.media.length - 1;
          }
          this.renderMedia(this);
          this.handleThumbnails();
          this.handleZoomGallery();
      };
      if (prevButton) {
        prevButton.addEventListener('click', () => updateMediaIndex(-1));
      }
      if (nextButton) {
        nextButton.addEventListener('click', () => updateMediaIndex(1));
      }
    }
  })
}

// Product info subcomponent
if (!customElements.get('component-pdp-product-info')) {
  customElements.define('component-pdp-product-info', class PDPProductInfo extends PDPProductSubComponent {
    constructor() {
      super();
      this.props = {
        bdEnabled: this.dataset.bdEnabled === 'true',
        showPriceBadge: this.dataset.showPriceBadge,
        priceBadgeSetsTag: this.dataset.priceBadgeSetsTag
      }
      this.state = {};
      if (this.props.bdEnabled) {
        this.props.bdDiscountType = this.dataset.bdDiscountType;
        this.state.bdAmountToSave = this.dataset.bdAmountToSave;
        this.state.bdItemsCount = this.dataset.bdItemsCount;
        this.props.bdTag = this.dataset.bdTag;
        this.props.bdPdpLabelAllTiersEnabled = this.dataset.bdPdpLabelAllTiersEnabled === 'true';
        this.props.bdFirstItemDiscount = this.dataset.bdFirstItemDiscount;
        this.props.bdSecondItemDiscount = this.dataset.bdSecondItemDiscount;
        this.props.bdThirdItemDiscount = this.dataset.bdThirdItemDiscount;
        this.props.bdFourthItemDiscount = this.dataset.bdFourthItemDiscount;
        this.props.bdFifthItemDiscount = this.dataset.bdFifthItemDiscount;
      }
    }
    render() {
      const template = this.template.content.cloneNode(true);
      // Set title and reviews
      const product = this.getProduct();
      if (product?.eyebrow) {
        if (product.eyebrow) {
          // Set eyebrow text
          template.querySelector('.js-eyebrow').textContent = product.eyebrow;
          // Set eyebrow collection link
          try {
            const eyebrowCollections = document.querySelector('.js-eyebrow-collections');
            if (eyebrowCollections) {
              const jsonObj = JSON.parse(eyebrowCollections.innerHTML);
              if (Object.keys(jsonObj).length !== 0) {
                const eyebrowKey = Object.keys(jsonObj).find(key => key === product.eyebrow.toLowerCase().trim());
                if (jsonObj[eyebrowKey]) {
                  template.querySelector('.js-eyebrow-link').href = jsonObj[eyebrowKey];
                } else {
                  template.querySelector('.js-eyebrow-link').removeAttribute('href');
                }
              }
            }
          } catch (e) {
            console.error('Failed to parse eyebrow collections JSON:', e);
          }
        }
        if (product.style) {
          template.querySelector('.js-style').textContent = product.style;
        }
        const reviewStars = `<component-review-stars data-reviews='${product.reviews_average}' data-total="${product.reviews_count}" data-type="product"></component-review-stars>`;
        template.querySelector('.js-review-stars').innerHTML = reviewStars;
      }
      // Set pricing
      if (product) {
        const price = product.price;
        const compare_at_price = product.compare_at_price;
        template.querySelector('.js-price').innerHTML = toMoney(price);
        if (parseInt(compare_at_price) > parseInt(price)) {
          template.querySelector('.js-compare-price').innerHTML = toMoney(compare_at_price);
          template.querySelector('.js-price').classList.add('price--sale');
          template.querySelector('.js-compare-price').classList.remove('hidden');
        } else {
          template.querySelector('.js-compare-price').classList.add('hidden');
          template.querySelector('.js-price').classList.remove('price--sale');
        }
        this.renderPriceBadge(template, compare_at_price, price);
        // Afterpay
        if (template.querySelector('.js-afterpay')) {
          if ((price) > parseFloat(template.querySelector('.js-afterpay').dataset.minPrice)) {
            template.querySelector('.js-afterpay-price').innerHTML = toMoney(price / parseInt(template.querySelector('.js-afterpay-available').dataset.installments));
            template.querySelector('.js-afterpay-available').classList.remove('hidden');
            template.querySelector('.js-afterpay-unavailable').classList.add('hidden');
          } else {
            template.querySelector('.js-afterpay-available').classList.add('hidden');
            template.querySelector('.js-afterpay-unavailable').classList.remove('hidden');
          }
        }
      }
      if (this.props.bdEnabled && template.querySelector('.js-bd-text')) {
        this.renderBundleDiscountPromo(template);
      }
      if (this.props.hydration && this.querySelector('.js-template')) this.querySelector('.js-template').remove();
      this.append(template);
      this.afterRender();
    }
    afterRender() {
      this.querySelector('.js-review-stars').addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('scroll-to-reviews'));
        if (document.querySelector('.js-product-reviews-desktop')) {
          document.querySelector('.js-product-reviews-desktop').scrollIntoView();
        }
      });
      if (this.props.bdEnabled && this.querySelector('.js-bd-text')) {
        window.addEventListener('update-bd-text', (data) => {
          if (data.detail && data.detail.amountToSave) {
            this.state.bdAmountToSave = data.detail.amountToSave;
            this.state.bdItemsCount = data.detail.bdItemsCount;
          }
          this.renderBundleDiscountPromo(this);
        });
      }
    }
    renderPriceBadge(template, comparePrice, price) {
      if (this.props.showPriceBadge !== 'none' && template.querySelector('.js-template-price-badge')) {
        const priceBadgeTemplate = template.querySelector('.js-template-price-badge').content.cloneNode(true);
        if (comparePrice > price) {
          priceBadgeTemplate.querySelectorAll('.js-price-badge-percent').forEach(percent => percent.innerHTML = `${Math.floor((100 - price * 100 / comparePrice))}%`);
          if (this.props.showPriceBadge === 'all' || (this.props.showPriceBadge === 'sets_only' && this.getProduct().tags.includes(this.props.priceBadgeSetsTag))) {
            if(this.querySelector('.js-pdp-price-right-hand-side')) {
              template.querySelector('.js-pdp-price-right-hand-side').appendChild(priceBadgeTemplate);
            } else {
              template.querySelector('.js-price-container').appendChild(priceBadgeTemplate);
            }
          }
        }
      }
    }
    renderBundleDiscountPromo(template) {


      const bdTextEl = template.querySelector('.js-bd-text');

      if(this.props.bdPdpLabelAllTiersEnabled) {

        if(bdTextEl) {

          bdTextEl.innerHTML = '';
          let firstItemDiscount = this.props.bdFirstItemDiscount;
          let secondItemDiscount = this.props.bdSecondItemDiscount;
          let thirdItemDiscount = this.props.bdThirdItemDiscount;
          let fourthItemDiscount = this.props.bdFourthItemDiscount;
          let fifthItemDiscount = this.props.bdFifthItemDiscount;
          let template = this.props.bdPdpLabelTemplate ? this.props.bdPdpLabelTemplate : this.props.bdPdpLabelEmptyTemplate ? this.props.bdPdpLabelEmptyTemplate : '';

          if(template && this.getSiblingColor().tags.includes(this.props.bdTag)){

            let hasAvailableTiers = false;

            // Add all tiers to the template
            if(firstItemDiscount && firstItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 1).replace('#tier_ordinal', 'first').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${firstItemDiscount}%` : toMoney(firstItemDiscount)) + '<br>';
            }
            if(secondItemDiscount && secondItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 2).replace('#tier_ordinal', 'second').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${secondItemDiscount}%` : toMoney(secondItemDiscount)) + '<br>';
            }
            if(thirdItemDiscount && thirdItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 3).replace('#tier_ordinal', 'third').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${thirdItemDiscount}%` : toMoney(thirdItemDiscount)) + '<br>';
            }
            if(fourthItemDiscount && fourthItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 4).replace('#tier_ordinal', 'fourth').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${fourthItemDiscount}%` : toMoney(fourthItemDiscount)) + '<br>';
            }
            if(fifthItemDiscount && fifthItemDiscount != "0") {
              hasAvailableTiers = true;
              bdTextEl.innerHTML += template.replace('#product_type', this.props.bdProductType).replace('#tier_number', 5).replace('#tier_ordinal', 'fifth').replace('#discount', this.props.bdDiscountType === 'percentage' ? `${fifthItemDiscount}%` : toMoney(fifthItemDiscount)) + '<br>';
            }

            if(hasAvailableTiers) {
              bdTextEl.classList.remove('hidden');
            }

          } else {
            bdTextEl.classList.add('hidden');
          }
        }
      }
      else {

        const amount = this.state.bdAmountToSave;
        const count = this.state.bdItemsCount + 1;
        const ordinal = count > 4 ? 'fifth' : count === 4 ? 'fourth' : count === 3 ? 'third' : count === 2 ? 'second' : 'first';
        if (bdTextEl && amount) {
          if (amount === 'discount_reached' || isFinalSale(this.getProduct().tags) || !this.getProduct().tags.includes(this.props.bdTag)) {
            bdTextEl.innerHTML = '';
            bdTextEl.classList.add('hidden');
          } else if (count < 2 && this.props.bdPdpLabelEmptyTemplate) {
            bdTextEl.innerHTML = this.props.bdPdpLabelEmptyTemplate.replace('#product_type', this.props.bdProductType).replace('#tier_number', count).replace('#tier_ordinal', ordinal).replace('#discount', this.props.bdDiscountType === 'percentage' ? `${amount}%` : toMoney(amount));
            bdTextEl.classList.remove('hidden');
          } else if (this.props.bdPdpLabelTemplate) {
            bdTextEl.innerHTML = this.props.bdPdpLabelTemplate.replace('#product_type', this.props.bdProductType).replace('#tier_number', count).replace('#tier_ordinal', ordinal).replace('#discount', this.props.bdDiscountType === 'percentage' ? `${amount}%` : toMoney(amount));
            bdTextEl.classList.remove('hidden');
          } else {
            bdTextEl.classList.add('hidden');
          }
        }
      }
    }
  });
}

// Product variants subcomponent
if (!customElements.get('component-pdp-product-variants-simple')) {
  customElements.define('component-pdp-product-variants-simple', class PDPProductVariantsSimple extends PDPProductSubComponent {
    constructor() {
      super();

      this.props.replaceUrl = this.dataset.replaceUrl && this.dataset.replaceUrl === 'true' ? true : false;
      this.props.isLanding = this.dataset.isLanding === 'true';
      this.props.inventoryQuantityThreshold = +this.dataset.inventoryQuantityThreshold;
      this.props.inventoryQuantityMessage = this.dataset.inventoryQuantityMessage;
      this.props.outOfStockThreshold = +this.dataset.outOfStockThreshold || 0;

      this.isCanada = window.Shopify.country === 'CA';
    }
    render() {
      const template = this.template.content.cloneNode(true);
      this.renderOutOfStock(template);
      if (this.props.inventoryQuantityThreshold) {
        this.renderInventoryMessage(template);
      }
      this.renderFinalSale(template);
      if (this.querySelector('.js-skeleton')) this.querySelector('.js-skeleton').remove();
      this.append(template);
      this.afterRender();
    }
    renderOutOfStock(template) {
      if (this.isOutOfStock() || this.getProduct().tags.includes('__label: Coming Soon')) {
        let showBIS = true;
        if (this.getProduct().tags.includes('bis-hidden') || this.getProduct().tags.includes('bis-paused')) {
          showBIS = false;
        }
        if(this.getProduct().tags.includes('__label: Coming Soon')) {
          showBIS = true;
        }
        if (showBIS && template.querySelector('.js-back-in-stock') && this.getVariant()) {
          template.querySelector('.js-out-of-stock').classList.remove('hidden');
          template.querySelector('.js-add-to-bag').disabled = true;
          template.querySelector('.js-add-to-bag').classList.add('hidden');
          template.querySelector('.js-add-to-bag-bundle-button').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-button').classList.add('hidden');
          template.querySelector('.js-add-to-bag-bundle-discount-button').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-discount-button').classList.add('hidden');
          const backInStockTemplate = template.querySelector('.js-template-back-in-stock').content.cloneNode(true);
          backInStockTemplate.querySelector('component-pdp-product-back-in-stock').dataset.productId = this.getProduct().id;
          backInStockTemplate.querySelector('component-pdp-product-back-in-stock').dataset.variantId = this.getVariant().id;
          template.querySelector('.js-back-in-stock').appendChild(backInStockTemplate);
        } else {
          template.querySelector('.js-out-of-stock').classList.remove('hidden');
          template.querySelector('.js-add-to-bag').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-button').disabled = true;
          template.querySelector('.js-add-to-bag-bundle-discount-button').disabled = true;
        }
      } else {
        template.querySelector('.js-out-of-stock').classList.add('hidden');
        template.querySelector('.js-add-to-bag').disabled = false;
        template.querySelector('.js-add-to-bag-bundle-button').disabled = false;
        template.querySelector('.js-add-to-bag-bundle-discount-button').disabled = false;
        template.querySelector('.js-add-to-bag').innerHTML = template.querySelector('.js-add-to-bag').dataset.addToBagText;
      }
    }
    renderInventoryMessage(template) {
      if (this.getVariant() && !this.isOutOfStock() && this.getVariant().inventory_management === 'shopify' && this.getVariant().inventory_policy === 'deny' && Number(this.getVariant().inventory_quantity) < this.props.inventoryQuantityThreshold && this.getVariant().inventory_quantity > 0) {
        template.querySelector('.js-inventory-message').textContent = this.props.inventoryQuantityMessage.replace('#', this.getVariant().inventory_quantity);
        template.querySelector('.js-inventory-message').classList.remove('hidden');
      } else {
        template.querySelector('.js-inventory-message').classList.add('hidden');
      }
      if (this.getProduct().tags.includes('__label: Coming Soon')) {
        template.querySelector('.js-inventory-message').classList.add('hidden');
      }
    }
    renderFinalSale(template) {
      if (!this.isOutOfStock() && isFinalSale(this.getProduct().tags)) {
        template.querySelector('.js-final-sale').classList.remove('hidden');
      } else {
        template.querySelector('.js-final-sale').classList.add('hidden');
      }
    }
    updateLoyaltyBanner() {
      document.dispatchEvent(new CustomEvent('pdp-loyalty-banner-updated', { detail: { price: this.getVariant()?.price || this.getProduct()?.price * 100 }}));
    }
    handleBundleButtonDiscountKit(color) {
      let bundleButtonEl = this.querySelector('.js-add-to-bag-bundle-discount-button');
      let bundleSaveTextEl = this.querySelector('.js-bundle-save-text');

      if (bundleButtonEl) {
        const bbPdpEnabled = bundleButtonEl.dataset.bbPdpEnabled === 'true';
        const bbParamEnabled = bundleButtonEl.dataset.bbParamEnabled === 'true';
        const bbId = bundleButtonEl.dataset.bbId || '';
        let tags = this.dom.parentComponent.state.product.colors[color].tags;

        // Check if is CA
        let isCa = window.Shopify.country === 'CA';
        let dkBBDiscountTag = isCa ? window.cart_settings.dkBBDiscountTagCa : window.cart_settings.dkBBDiscountTag;

        // If US I need to avoid the tag 'bundle-discount-button-ca-', checking is the tag does not contain 'bundle-discount-button-ca-'
        if (!isCa) {
          tags = tags.filter(tag => !tag.includes(window.cart_settings.dkBBDiscountTagCa));
        }

        let tag = tags.find(tag => tag.includes(dkBBDiscountTag));

        if (bbPdpEnabled && tag) {
          const quantityPriceText = tag.split(dkBBDiscountTag)[1];
          const quantity = +quantityPriceText.split('-')[0];
          const priceUsd = +quantityPriceText.split('-')[1] * 100;
          const priceCad = +quantityPriceText.split('-')[1] * 100;
          let textContent;
          if (window.Shopify.country === 'CA') {
            textContent = `${quantity} for ${toMoney(priceCad)} ${window.Shopify.currency.active}`;
          } else {
            textContent = `${quantity} for ${toMoney(priceUsd)}`;
          }
          bundleButtonEl.dataset.bbQuantity = quantity;
          bundleButtonEl.dataset.bbPrice = priceUsd;
          bundleButtonEl.dataset.bbPriceCa = priceCad;
          if (bbParamEnabled && bbId) {
            const searchParams = new URLSearchParams(window.location.search);
            const bbCookie = jsCookie.get('_tl_bb_id');
            const bundleButtonParamEnabled = searchParams.get('bundleButton') || bbCookie;
            if (bundleButtonParamEnabled === bbId) {
              bundleButtonEl.textContent = textContent;
              bundleButtonEl.classList.remove('hidden');
              bundleSaveTextEl ? bundleSaveTextEl.classList.remove('hidden') : null;
              if (!bbCookie || (bbCookie && bbCookie !== bundleButtonParamEnabled)) window.setCookie('_tl_bb_id', bbId, { expires: 1 });
            } else {
              bundleButtonEl.classList.add('hidden');
              bundleSaveTextEl ? bundleSaveTextEl.classList.add('hidden') : null;
              jsCookie.remove('_tl_bb_id');
            }
          } else {
            bundleButtonEl.textContent = textContent;
            bundleButtonEl.classList.remove('hidden');
            bundleSaveTextEl ? bundleSaveTextEl.classList.remove('hidden') : null;
          }
        } else {
          bundleButtonEl.classList.add('hidden');
          bundleSaveTextEl ? bundleSaveTextEl.classList.add('hidden') : null;
        }

        bundleButtonEl.addEventListener('click', async (event) => {
          event.preventDefault();

          try {
            const variant = this.getVariant();

            if (!variant) {
              throw new Error('No variant selected');
            }

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.loadingText;
            bundleButtonEl.classList.add('add-to-bag-btn--loading');

            const bundleButtonId = keygen('bundle-button');
            const product = this.getProduct().colors[this.dom.parentComponent.state.selectedColor];
            const payload = {
              id: variant.id,
              quantity: +bundleButtonEl.dataset.bbQuantity,
              properties: {
                _dkQuantity: +bundleButtonEl.dataset.bbQuantity,
                _dkPrice: +bundleButtonEl.dataset.bbPrice,
                _dkPriceCa: +bundleButtonEl.dataset.bbPriceCa,
                _tags: filterProductTags(product.tags),
                _isBundleButtonDiscountKit: true
              }
            };

            const price = variant.price;
            const compareAtPrice = variant.compare_at_price;
            if (window.Shopify.country === 'CA' && compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = Math.trunc((compareAtPrice / 100) / window.i18n_cad_exchange_rate) * 100;
              payload.properties._compareAtPriceCa = compareAtPrice;
            } else if (compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = compareAtPrice;
              payload.properties._compareAtPriceCa = Math.ceil((compareAtPrice / 100) * window.i18n_cad_exchange_rate) * 100;
            }

            await window.cartManager.addItem(payload);

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            this.querySelector('.js-add-to-bag-error').classList.add('hidden');

            window.sendGTMData && window.sendGTMData({
              event: 'bundle_button_add_to_cart',
              variant_id: variant?.id,
              product_id: product.id,
              bundle_button_id: bundleButtonId,
              bundle_button_quantity: bundleButtonEl.dataset.bbQuantity,
              bundle_button_price_usd: bundleButtonEl.dataset.bbPrice,
              bundle_button_price_cad: bundleButtonEl.dataset.bbPriceCa,
              source: 'pdp'
            });

            window.dispatchEvent(new CustomEvent('update-cart'));
          } catch (err) {
            console.error('ERROR: ', err);
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            let errorMsg;
            if (typeof err === 'string' && err.toLowerCase().includes('out of stock')) {
              errorMsg = 'Out of stock';
            } else if (typeof err === 'string' && err.toLowerCase().includes('cannot apply selling plan')) {
              errorMsg = bundleButtonEl.dataset.subscriptionError;
            } else if (this.hasBandCup()) {
              if (!this.state.bandSize && !this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.sizeError;
              } else if (this.state.bandSize && !this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.cupSizeError;
              } else if (!this.state.bandSize && this.state.cupSize) {
                errorMsg = bundleButtonEl.dataset.bandSizeError;
              }
            } else if (!this.hasBandCup()) {
              if (!this.dom.parentComponent.state.selectedSize) {
                errorMsg = bundleButtonEl.dataset.sizeError;
              }
            }

            if (!errorMsg) {
              errorMsg = bundleButtonEl.dataset.cartError;
            }
            this.querySelector('.js-add-to-bag-error').innerHTML = errorMsg;
            this.querySelector('.js-add-to-bag-error').classList.remove('hidden');
          }
        });
      }
    }
    handleBundleButton() {
      const bundleButtonEl = this.querySelector('.js-add-to-bag-bundle-button');

      if (bundleButtonEl) {
        const bbPdpEnabled = bundleButtonEl.dataset.bbPdpEnabled === 'true';
        const bbParamEnabled = bundleButtonEl.dataset.bbParamEnabled === 'true';
        const bbId = bundleButtonEl.dataset.bbId || '';
        const tags = this.dom.parentComponent.state.product.tags;
        const tag = tags.find(tag => tag.includes('bundle-button-'));
        if (bbPdpEnabled && tag) {
          const quantityPriceText = tag.split('bundle-button-')[1];
          const quantity = +quantityPriceText.split('-')[0];
          const priceUsd = +quantityPriceText.split('-')[1];
          const priceCad = Math.round(priceUsd * window.i18n_cad_exchange_rate);
          let textContent;
          if (window.Shopify.country === 'CA') {
            textContent = `${quantity} for ${toMoney(priceCad)} ${window.Shopify.currency.active}`;
          } else {
            textContent = `${quantity} for ${toMoney(priceUsd)}`;
          }
          bundleButtonEl.dataset.bbQuantity = quantity;
          bundleButtonEl.dataset.bbPrice = priceUsd;
          bundleButtonEl.dataset.bbPriceCa = priceCad;
          if (bbParamEnabled && bbId) {
            const searchParams = new URLSearchParams(window.location.search);
            const bbCookie = jsCookie.get('_tl_bb_id');
            const bundleButtonParamEnabled = searchParams.get('bundleButton') || bbCookie;
            if (bundleButtonParamEnabled === bbId) {
              bundleButtonEl.textContent = textContent;
              bundleButtonEl.classList.remove('hidden');
              if (!bbCookie || (bbCookie && bbCookie != bundleButtonParamEnabled)) window.setCookie('_tl_bb_id', bbId, { expires: 1 });
            } else {
              bundleButtonEl.classList.add('hidden');
              jsCookie.remove('_tl_bb_id');
            }
          } else {
            bundleButtonEl.textContent = textContent;
            bundleButtonEl.classList.remove('hidden');
          }
        } else {
          bundleButtonEl.classList.add('hidden');
        }

        bundleButtonEl.addEventListener('click', async (event) => {
          event.preventDefault();

          try {
            const variant = this.getVariant();

            if (!variant) {
              throw new Error('No variant selected');
            }

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.loadingText;
            bundleButtonEl.classList.add('add-to-bag-btn--loading');

            const bundleButtonId = keygen('bundle-button');
            const product = this.getProduct();
            const payload = {
              id: variant.id,
              quantity: +bundleButtonEl.dataset.bbQuantity,
              properties: {
                _bbId: bundleButtonId,
                _bbQuantity: +bundleButtonEl.dataset.bbQuantity,
                _bbPrice: +bundleButtonEl.dataset.bbPrice,
                _bbPriceCa: +bundleButtonEl.dataset.bbPriceCa,
                _tags: filterProductTags(product.tags),
                _isBundleButton: false
              }
            };

            const price = variant.price;
            const compareAtPrice = variant.compare_at_price;
            if (window.Shopify.country === 'CA' && compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = Math.trunc((compareAtPrice / 100) / window.i18n_cad_exchange_rate);
              payload.properties._compareAtPriceCa = compareAtPrice;
            } else if (compareAtPrice && compareAtPrice > price) {
              payload.properties._compareAtPrice = compareAtPrice;
              payload.properties._compareAtPriceCa = Math.ceil((compareAtPrice / 100) * window.i18n_cad_exchange_rate);
            }

            await window.cartManager.addItem(payload);

            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            this.querySelector('.js-add-to-bag-error').classList.add('hidden');

            window.sendGTMData && window.sendGTMData({
              event: 'bundle_button_add_to_cart',
              variant_id: variant?.id,
              product_id: product.id,
              bundle_button_id: bundleButtonId,
              bundle_button_quantity: bundleButtonEl.dataset.bbQuantity,
              bundle_button_price_usd: bundleButtonEl.dataset.bbPrice,
              bundle_button_price_cad: bundleButtonEl.dataset.bbPriceCa,
              source: 'pdp'
            });

            window.dispatchEvent(new CustomEvent('update-cart'));
          } catch (err) {
            console.error('ERROR: ', err);
            bundleButtonEl.classList.remove('add-to-bag-btn--loading');
            bundleButtonEl.innerHTML = bundleButtonEl.dataset.addToBagText;
            let errorMsg;
            if (typeof err === 'string' && err.toLowerCase().includes('out of stock')) {
              errorMsg = 'Out of stock';
            } else if (typeof err === 'string' && err.toLowerCase().includes('cannot apply selling plan')) {
              errorMsg = bundleButtonEl.dataset.subscriptionError;
            } else {
              errorMsg = bundleButtonEl.dataset.cartError;
            }
            this.querySelector('.js-add-to-bag-error').innerHTML = errorMsg;
            this.querySelector('.js-add-to-bag-error').classList.remove('hidden');
          }
        });
      }
    }
    afterRender() {
      if (document.querySelector('.js-variants-accordions-container')) {
        document.querySelector('.js-variants-accordions-container').classList.remove('hidden');
      }
      this.handleAddToBag();
    }
    async handleAddToBag() {
      this.querySelector('.js-add-to-bag').addEventListener('click', async (event) => {
        event.preventDefault();

        try {
          // Prevent multiple clicks
          if (this.querySelector('.js-add-to-bag').classList.contains('add-to-bag-btn--loading')) {
            return;
          }

          const variant = this.getVariant();

          if (!variant) {
            throw new Error('No variant selected');
          }

          this.querySelector('.js-add-to-bag').innerHTML = this.querySelector('.js-add-to-bag').dataset.loadingText;
          this.querySelector('.js-add-to-bag').classList.add('add-to-bag-btn--loading');

          const payload = {
            id: variant.id,
            quantity: 1,
            properties: {
              _tags: filterProductTags(this.getProduct()?.tags)
            }
          };

          if (this.querySelector('input[name="selling_plan"]') && this.querySelector('input[name="selling_plan"]').value) {
            payload.selling_plan = Number(this.querySelector('input[name="selling_plan"]').value);
          }

          const price = variant.price;
          const compareAtPrice = variant.compare_at_price;
          if (window.Shopify.country === 'CA' && compareAtPrice && compareAtPrice > price) {
            payload.properties._compareAtPrice = Math.trunc((compareAtPrice / 100) / window.i18n_cad_exchange_rate);
            payload.properties._compareAtPriceCa = compareAtPrice;
          } else if (compareAtPrice && compareAtPrice > price) {
            payload.properties._compareAtPrice = compareAtPrice;
            payload.properties._compareAtPriceCa = Math.ceil((compareAtPrice / 100) * window.i18n_cad_exchange_rate);
          }

          if (window.cart_settings?.checkoutFinalSaleLabel && payload.properties._tags?.some(tag => tag.toLowerCase().replaceAll(/\s|-|_/g, '').includes('finalsale'))) {
            payload.properties['Final Sale'] = window.cart_settings.checkoutFinalSaleLabel;
          }

          await window.cartManager.addItem(payload);

          this.querySelector('.js-add-to-bag').innerHTML = this.querySelector('.js-add-to-bag').dataset.addToBagText;
          this.querySelector('.js-add-to-bag').classList.remove('add-to-bag-btn--loading');
          this.querySelector('.js-add-to-bag-error').classList.add('hidden');

          window.dispatchEvent(new CustomEvent('update-cart'));
        } catch (err) {
          console.error('ERROR: ', err);
          this.querySelector('.js-add-to-bag').classList.remove('add-to-bag-btn--loading');
          this.querySelector('.js-add-to-bag').innerHTML = this.querySelector('.js-add-to-bag').dataset.addToBagText;
          let errorMsg;
          if (typeof err === 'string' && err.toLowerCase().includes('out of stock')) {
            errorMsg = 'Out of stock';
          } else if (typeof err === 'string' && err.toLowerCase().includes('cannot apply selling plan')) {
            errorMsg = this.querySelector('.js-add-to-bag').dataset.subscriptionError;
          } else {
            errorMsg = this.querySelector('.js-add-to-bag').dataset.cartError;
          }
          this.querySelector('.js-add-to-bag-error').innerHTML = errorMsg;
          this.querySelector('.js-add-to-bag-error').classList.remove('hidden');
        }
      });
      this.handleBundleButton(this.dom.parentComponent.state.selectedColor);
      this.handleBundleButtonDiscountKit(this.dom.parentComponent.state.selectedColor);
    }
    isOutOfStock() {
      if (this.getVariant().inventory_policy === 'continue') {
        return false;
      } else if (!this.getVariant().available) {
        return true;
      } else if (this.getVariant().inventory_quantity <= this.props.outOfStockThreshold && !this.getProduct().tags.includes('inventory_threshold_ignore')) {
        return true;
      }
      return false;
    }
  });
}

// Product details accordion subcomponent
if (!customElements.get('component-pdp-product-accordion')) {
  customElements.define('component-pdp-product-accordion', class PDPProductAccordion extends PDPProductSubComponent {
    constructor() {
      super();
      this.state = {
        expanded: (window.innerWidth <= 1024 ? this.dataset.expandedMob : this.dataset.expanded) === "true"
      }
      this.props.type = this.dataset.type;
      this.props.showProductDetailsIcons = this.dataset.showProductDetailsIcons === 'true';
    }
    render() {
      const template = this.template.content.cloneNode(true);
      const icon = document.createElement("span");
      icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.21967 9.21967C7.51256 8.92678 7.98744 8.92678 8.28033 9.21967L11.75 12.6893L15.2197 9.21967C15.5126 8.92678 15.9874 8.92678 16.2803 9.21967C16.5732 9.51256 16.5732 9.98744 16.2803 10.2803L12.2803 14.2803C12.1397 14.421 11.9489 14.5 11.75 14.5C11.5511 14.5 11.3603 14.421 11.2197 14.2803L7.21967 10.2803C6.92678 9.98744 6.92678 9.51256 7.21967 9.21967Z" fill="#2C272D"></path></svg>';
      if (template.querySelector('.js-accordion-icon')) template.querySelector('.js-accordion-icon').appendChild(icon);
      let accordionContent;
      if (this.props.type === "product_details") {
        // Render product detail icons
        if (this.props.showProductDetailsIcons && this.getProduct().product_details_icons) {
          template.querySelectorAll('.js-product-details-icons > *:not(template)').forEach(item => item.remove());
          for (let icon of this.getProduct().product_details_icons) {
            if (icon.image && icon.text) {
              const iconTemplate = template.querySelector('.js-template-product-detail-icon').content.cloneNode(true);
              iconTemplate.querySelector('.js-product-detail-img').dataset.src = icon.image.src;
              iconTemplate.querySelector('.js-product-detail-img').dataset.sizes = 'auto';
              iconTemplate.querySelector('.js-product-detail-img').width = icon.image.width;
              iconTemplate.querySelector('.js-product-detail-img').height = icon.image.height;
              iconTemplate.querySelector('.js-product-detail-img').classList.add('lazyload');
              iconTemplate.querySelector('.js-product-details-icon-text').innerHTML = icon.text;
              template.querySelector('.js-product-details-icons').appendChild(iconTemplate);
            }
          }
        }
        if (this.getProduct()?.accordions?.product_details) {
          accordionContent = this.getProduct().accordions.product_details;
        } else {
          accordionContent = this.getProduct().description;
        }
      } else if (this.props.type === "size_fit") {
        accordionContent = this.getProduct()?.accordions?.size_fit;
      } else if (this.props.type === "fabric_care") {
        accordionContent = this.getProduct()?.accordions?.fabric_care;
      } else {
        accordionContent = template.querySelector('.js-accordion-content') ? template.querySelector('.js-accordion-content').innerHTML : null;
      }

      if (accordionContent) {
        template.querySelector('.js-accordion-content').innerHTML = accordionContent;
        this.append(template);
        this.afterRender();
      }
      if (this.state.expanded && this.querySelector('.js-accordion-details')) {
        this.querySelector('.js-accordion-details').open = true;
      }

    }
    afterRender() {
      this.querySelector('.js-accordion-details').addEventListener('click', () => {
        this.state.expanded = !this.state.expanded;
        this.sendAccordionGTMData();
      });
      if (this.props.type === "reviews") {
        document.addEventListener('scroll-to-reviews', () => {
          this.state.expanded = true;
          this.querySelector('.js-accordion-details').open = true;
          this.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" });
        });
      }
    }
    sendAccordionGTMData() {
      window.sendGTMData && window.sendGTMData({
        event: `pdp_accordion_${this.props.type}`,
        product_id: this.getProduct().id,
        expanded: this.state.expanded,
        source: 'pdp'
      });
    }
  });
}

// Back in stock component
if (!customElements.get('component-pdp-product-back-in-stock')) {
  customElements.define('component-pdp-product-back-in-stock', class PDPProductBackInStock extends HTMLElement {
    constructor() {
      super();
      this.state = {
        acceptsMarketing: this.dataset.bisKlaviyoEnabled === 'true',
        email: ''
      }
      this.props = {
        variantId: this.dataset.variantId,
        productId: this.dataset.productId,
        disServiceKey: JSON.parse(this.dataset.pskey).disServiceKey,
        cryptoSecret: this.dataset.cryptoSecret,
        bisKlaviyoEnabled: this.dataset.bisKlaviyoEnabled === 'true',
        bisKlaviyoPublicKey: this.dataset.bisKlaviyoPublicKey || '',
        bisKlaviyoRevision: this.dataset.bisKlaviyoRevision || '2024-10-15',
        bisKlaviyoListId: this.dataset.bisKlaviyoListId || ''
      }
      this.render();
    }
    render() {
      const template = this.querySelector('template').content.cloneNode(true);
      this.append(template);
      this.afterRender();
    }
    afterRender() {
      this.querySelector('.js-email-input').addEventListener('input', input => {
        this.state.email = input.srcElement.value;
      });
      this.querySelector('.js-subscribe-checkbox').addEventListener('change', checkbox => {
        this.state.acceptsMarketing = checkbox.srcElement.checked;
      });
      this.querySelector('.js-form').addEventListener('submit', async (event) => {
        event.preventDefault();

        // Identify user on Klaviyo
        window.klaviyoIdentify && window.klaviyoIdentify(this.state.email);

        if (this.state.acceptsMarketing) {
          await this.registerProspect();
        }
        if (this.props.bisKlaviyoEnabled) {
          if (this.props.bisKlaviyoListId) {
            await this.registerToNewsletterInKlaviyo(this.state.email, this.props.bisKlaviyoListId);
          }
          await this.registerBackInStockInKlaviyo(this.state.email);
        } else {
          await this.registerBackInStock();
        }
      });
    }
    async sendEmailSubscribeGTMEvent() {
      await import(document.querySelector('.js-component-pdp-product').dataset.browserCrypto);

      const cipherInstance = browserCrypto.createCipher(
        'aes-256-cbc',
        this.props.cryptoSecret,
      );
      const encryptedEmail = cipherInstance.update(this.state.email, 'utf8', 'hex');
      const finalEmailHash = encryptedEmail + cipherInstance.final('hex');

      window.sendGTMData && window.sendGTMData({
        event: 'email_subscribe',
        email: finalEmailHash,
        source: 'pdp_capture_back_in_stock'
      });
    }
    async registerProspect() {
      try {
        const data = {
          email: this.state.email,
          source: 'pdp_capture',
          user_locale: window.userLocale
        };
        const url = window.location.hostname.includes('trythirdlove.com') ? 'https://dis-apis.trythirdlove.com/v1/prospects/registration' : 'https://dis-apis.thirdlove.com/v1/prospects/registration';
        let response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.props.disServiceKey,
          },
          method: 'POST',
          body: JSON.stringify(data)
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (errDis) {
        console.error('Error while sending new prospect to DIS', errDis);
      }
    }
    async registerToNewsletterInKlaviyo(email, listId) {
      try {
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PDP',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId
                }
              }
            }
          }
        };

        const options = {
          method: 'POST',
          headers: {revision: this.props.bisKlaviyoRevision, 'content-type': 'application/vnd.api+json'},
          body: JSON.stringify(payload)
        };

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${this.props.bisKlaviyoPublicKey}`, options);
        if (!response.ok) {
          const jsonResponse = await response.json();
          throw new Error(jsonResponse);
        }
      } catch (err) {
        console.error('Error subscribing user to the newsletter: ', JSON.stringify(err));
      }
    }
    async registerBackInStock() {
      let response;
      try {
        response = await BIS.create(this.state.email, this.props.variantId, this.props.productId, {
          accepts_marketing: this.state.acceptsMarketing,
          quantity_required: 1,
          customer_utc_offset: new Date().getTimezoneOffset()
        });
        if (response.status !== 'OK' && !response.errors?.base[0]?.includes('already registered')) {
          throw new Error(response);
        } else {
          if (this.state.acceptsMarketing) {
            await this.sendEmailSubscribeGTMEvent();
          }
          this.querySelector('.js-content').classList.add('hidden');
          this.querySelector('.js-error-msg').classList.add('hidden');
          this.querySelector('.js-success-msg').classList.remove('hidden');
        }
      } catch (errBis) {
        console.error('Error while sending information to BIS App', errBis?.errors?.base);
        this.querySelector('.js-error-msg').classList.remove('hidden');
        this.querySelector('.js-success-msg').classList.add('hidden');
      }
    }
    async registerBackInStockInKlaviyo(email) {
      try {
        const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.props.bisKlaviyoPublicKey}`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/vnd.api+json',
            'revision': this.props.bisKlaviyoRevision
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['email'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email
                    }
                  }
                }
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: `$shopify:::$default:::${this.props.variantId}`
                  }
                }
              }
            }
          })
        });
        if (!response.ok) {
          throw new Error(response);
        } else {
          this.querySelector('.js-content').classList.add('hidden');
          this.querySelector('.js-error-msg').classList.add('hidden');
          this.querySelector('.js-success-msg').classList.remove('hidden');
        }
      } catch (err) {
        console.error('Back in stock subscription error: ', err);
        this.querySelector('.js-error-msg').classList.remove('hidden');
        this.querySelector('.js-success-msg').classList.add('hidden');
      }
    }
  });
}

if (!customElements.get('component-pdp-videos')) {
  customElements.define('component-pdp-videos', class ProductVideos extends HTMLElement {
    constructor() {
      super();
      this.state = {
        selectedVideoId: null
      }
      this.props = {
        productUrl: this.dataset.productUrl
      }
    }
    connectedCallback() {
      this.checkSafari();
      if (window.location.href.includes('videoId')) {
        const urlParams = new URLSearchParams(window.location.search);
        const videoId = urlParams.get('videoId');
        if (videoId) {
          this.state.selectedVideoId = videoId;
          this.render(true);
          this.afterRender();
        }
      }
      this.querySelectorAll('.js-pdp-video').forEach(video => {
        video.addEventListener('click', () => {
          this.state.selectedVideoId = video.dataset.mainVideoId;
          this.render();
          this.afterRender();
        });
      });
      this.handleScroll();
    }
    handleScroll() {
      const container = this.querySelector('.js-pdp-videos');
      const next = this.querySelector('.js-videos-next');
      const prev = this.querySelector('.js-videos-prev');
      if (container && next && prev) {
        if (container.scrollWidth > container.clientWidth) {
          next.classList.remove('disabled');
        }
        container.addEventListener('scroll', () => {
          if (container.scrollLeft > 0) {
            prev.classList.remove('disabled');
          } else {
            prev.classList.add('disabled');
          }
          // Hide next button if scrolled to end of slider
          if (container.scrollLeft < (container.scrollWidth - container.offsetWidth - 10)) {
            next.classList.remove('disabled');
          } else {
            next.classList.add('disabled');
          }
        });
        prev.addEventListener('click', () => {
          container.scrollBy({ top: 0, left: -325, behavior: 'smooth' });
        });
        next.addEventListener('click', () => {
          container.scrollBy({ top: 0, left: 325, behavior: 'smooth' });
        });
      }
    }
    checkSafari() {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        this.classList.add("safari");
      }
    }
    render(muted = false) {
      if (this.state.selectedVideoId) {
        const videoTemplate = this.querySelector('.js-template-main-video').content.cloneNode(true);
        const video = this.querySelector(`.js-pdp-video[data-main-video-id="${this.state.selectedVideoId}"]`);
        if (video && video.dataset.mainVideoSrc) {
          videoTemplate.querySelector('.js-main-video-source').src = video.dataset.mainVideoSrc;
          videoTemplate.querySelector('.js-main-video-source').type = video.dataset.mainVideoType;
          if (muted) {
            videoTemplate.querySelector('.js-main-video').muted = true;
          }
          this.querySelector('.js-main-video-container').innerHTML = '';
          this.querySelector('.js-main-video-container').append(videoTemplate);
        }
      }
    }
    afterRender() {
      if (this.querySelector('.js-main-video')) {
        this.querySelector('.js-main-video').load();
      }
      this.querySelector('.js-main-video-close').addEventListener('click', () => {
        this.querySelector('.js-main-video-container').innerHTML = '';
      });
      // Copy link to PDP with video id to clipboard
      this.querySelector('.js-share-video').addEventListener('click', () => {
        navigator.clipboard.writeText(`${this.props.productUrl}?videoId=${this.state.selectedVideoId}`);
        this.querySelector('.js-share-video-confirmation').classList.remove('hidden');
        setTimeout(() => {
          this.querySelector('.js-share-video-confirmation').classList.add('hidden');
        }, 2000);
      });
    }
  });
}
